import { createContext, useContext, useState, useCallback, ReactNode } from "react";
import ReferralModal from "@/components/ReferralModal";

interface ReferralContextType {
  triggerContactWithReferral: (action: () => void) => void;
}

const ReferralContext = createContext<ReferralContextType | null>(null);

export const useReferral = () => {
  const context = useContext(ReferralContext);
  if (!context) {
    throw new Error("useReferral must be used within a ReferralProvider");
  }
  return context;
};

interface ReferralProviderProps {
  children: ReactNode;
}

export const ReferralProvider = ({ children }: ReferralProviderProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);

  const triggerContactWithReferral = useCallback((action: () => void) => {
    setPendingAction(() => action);
    setIsOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setPendingAction(null);
  }, []);

  const handleComplete = useCallback(() => {
    setIsOpen(false);
    if (pendingAction) {
      // Small delay to let modal close first
      setTimeout(() => {
        pendingAction();
      }, 100);
    }
    setPendingAction(null);
  }, [pendingAction]);

  return (
    <ReferralContext.Provider value={{ triggerContactWithReferral }}>
      {children}
      <ReferralModal
        isOpen={isOpen}
        onClose={handleClose}
        onComplete={handleComplete}
      />
    </ReferralContext.Provider>
  );
};
